/**
 * Dialog de clôture mensuelle bancaire (spec §13)
 * - Affiche la check-list (9 vérifications bloquantes)
 * - Bouton "Clôturer" désactivé tant que all_ok=false
 * - Backend: GET /api/bank/periods/{acct}/{yyyy_mm}/checklist
 *           POST /api/bank/periods/{acct}/{yyyy_mm}/close
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Lock, Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ClosePeriodDialog = ({ open, onClose, accountId, yyyyMm, onClosed }) => {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const loadChecklist = useCallback(async () => {
    if (!open || !accountId || !yyyyMm) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bank/periods/${accountId}/${yyyyMm}/checklist`);
      setChecklist(data);
    } catch (e) {
      toast({ title: 'Erreur chargement', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [open, accountId, yyyyMm]);

  useEffect(() => { loadChecklist(); }, [loadChecklist]);
  useEffect(() => { if (!open) setConfirmed(false); }, [open]);

  const doClose = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/bank/periods/${accountId}/${yyyyMm}/close`);
      toast({ title: 'Mois clôturé ✅', description: `${yyyyMm} verrouillé avec succès.` });
      onClosed?.(); onClose();
    } catch (e) {
      toast({ title: 'Échec clôture', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const canClose = checklist?.all_ok && confirmed;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="bank-close-period-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-blue-600" /> Clôturer le mois {yyyyMm}
          </DialogTitle>
          <DialogDescription>
            Vérifications préalables — tous les points doivent être validés pour clôturer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {loading && (
            <div className="text-xs text-zinc-500 py-4 text-center">
              <Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Chargement...
            </div>
          )}

          {checklist && checklist.checks.map(c => (
            <div key={c.key}
              className={`flex items-start gap-2 px-3 py-2 rounded-md border text-xs ${
                c.ok ? 'border-emerald-200 bg-emerald-50/40' : 'border-rose-200 bg-rose-50/40'
              }`}
              data-testid={`checklist-${c.key}`}>
              {c.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                    : <XCircle className="h-4 w-4 text-rose-600 flex-shrink-0" />}
              <span className={`flex-1 ${c.ok ? 'text-emerald-900' : 'text-rose-900'}`}>{c.label}</span>
              {'count' in c && c.count > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${c.ok ? 'bg-emerald-200 text-emerald-800' : 'bg-rose-200 text-rose-800'}`}>
                  {c.count}
                </span>
              )}
            </div>
          ))}

          {checklist && !checklist.all_ok && (
            <div className="mt-3 p-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 flex items-start gap-2">
              <ShieldAlert className="h-4 w-4 flex-shrink-0" />
              <span>Corrigez les blocages ci-dessus avant de pouvoir clôturer ce mois.</span>
            </div>
          )}

          {checklist && checklist.all_ok && (
            <label className="mt-3 flex items-start gap-2 p-2 rounded-md bg-blue-50 border border-blue-300 text-xs text-blue-900 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-0.5"
                data-testid="close-confirmation-checkbox"
              />
              <span>
                Je confirme vouloir clôturer <strong>{yyyyMm}</strong>. Après clôture, toute modification nécessitera un droit admin et une justification écrite.
              </span>
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Annuler</Button>
          <Button onClick={doClose} disabled={!canClose || submitting}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="close-period-submit">
            {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Lock className="h-4 w-4 mr-1" />}
            Clôturer le mois
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClosePeriodDialog;
